import React, { FunctionComponent, useEffect, useState } from 'react';
import { SharedIconCarrotDown } from '../../../../shared/icons';
import { utilNumberToCurrency } from 'src/utils';
import { FormattedMessage, useIntl } from 'react-intl';

interface CalculatorSquareAverageMeterComparisonProps {
  lowestPrice: number;
  highestPrice: number;
  indicatorPosition: number; // percentage (0-100)
  label: string;
}

const CalculatorSquareMeterAverageComparison: FunctionComponent<
  CalculatorSquareAverageMeterComparisonProps
> = ({ lowestPrice, highestPrice, indicatorPosition, label }) => {
  const intl = useIntl();
  const style: React.CSSProperties = {
    left: `${indicatorPosition}%`,
    marginLeft: `${indicatorPosition === 100 ? '-9px' : '-7px'}`
  };
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    setMessage(
      intl.formatMessage(
        {
          id: 'calculator.squareMeterAverage.comparisonMessage',
          defaultMessage: 'Vergeleken met de rest van Nederland {label} {position}.'
        },
        {
          label: `${
            indicatorPosition === 100 || indicatorPosition === 0 ? 'is' : ' ligt'
          } <strong>${label}</strong>`,
          position: getPositionLabel()
        }
      )
    );

    function getPositionLabel() {
      // translation objects with intl.formatMessage
      const translation = {
        belowAverage: intl.formatMessage({
          id: 'calculator.squareMeterAverage.belowAverage',
          defaultMessage: 'onder het gemiddelde'
        }),
        aboveAverage: intl.formatMessage({
          id: 'calculator.squareMeterAverage.aboveAverage',
          defaultMessage: 'boven het gemiddelde'
        }),
        netBelowAverage: intl.formatMessage({
          id: 'calculator.squareMeterAverage.netBelowAverage',
          defaultMessage: 'net onder het gemiddelde'
        }),
        netAboveAverage: intl.formatMessage({
          id: 'calculator.squareMeterAverage.netAboveAverage',
          defaultMessage: 'net boven het gemiddelde'
        }),
        onHighest: intl.formatMessage({
          id: 'calculator.squareMeterAverage.onHighest',
          defaultMessage: 'de duurste gemeente'
        }),
        onLowest: intl.formatMessage({
          id: 'calculator.squareMeterAverage.onLowest',
          defaultMessage: 'de goedkoopste gemeente'
        })
      };

      let positionLabel = translation.aboveAverage;

      if (indicatorPosition < 50) {
        positionLabel = translation.belowAverage;
      }

      if (Math.abs(indicatorPosition - 50) <= 20) {
        positionLabel =
          indicatorPosition < 50 ? translation.netBelowAverage : translation.netAboveAverage;
      }

      if (indicatorPosition === 100) {
        positionLabel = translation.onHighest;
      }

      if (indicatorPosition === 0) {
        positionLabel = translation.onLowest;
      }
      return positionLabel;
    }
  }, [indicatorPosition]);

  return (
    <>
      <p className="font-bold my-8">
        <FormattedMessage id={`search.compare.title`} defaultMessage={`De m2 prijs in Nederland`} />
      </p>
      <div className="flex">
        <div>
          <div className="mt-6">{utilNumberToCurrency(lowestPrice, true, 0)}</div>
        </div>

        <div className={`w-full mx-2 relative`}>
          <div className={`w-[98%] text-center`}>
            <div style={style} className="absolute bottom-[28px] -translate-x-[40%]">
              {label}
            </div>
          </div>
          <div className="absolute w-[98%] ml-[1%] border-0 bottom-[10px] border-b-2 border-dotted border-black"></div>
          <div style={style} className="absolute bottom-[14px]">
            <SharedIconCarrotDown boxSize={6} />
          </div>
          <div className="absolute r w-full bottom-[5px] flex justify-between">
            <div className="w-[2px] h-5 bg-black"></div>
            <div className="w-[2px] h-5 bg-black"></div>
            <div className="w-[2px] h-5 bg-black"></div>
          </div>
        </div>

        <div>
          <div className="mt-6">{utilNumberToCurrency(highestPrice, true, 0)}</div>
        </div>
      </div>
      <div className="flex justify-between">
        <div>
          <FormattedMessage id={`search.compare.lowest`} defaultMessage={`Laagste`} />
        </div>
        <div>
          <FormattedMessage id={`search.compare.highest`} defaultMessage={`Hoogste`} />
        </div>
      </div>
      <p className="my-8" dangerouslySetInnerHTML={{ __html: message }}></p>
    </>
  );
};

export default CalculatorSquareMeterAverageComparison;
